import { Agent, Node, SimulationStats, AgentState, AgentType, NodeType, SimulationConfig } from '../types';
import { findPath, findNearestNodeByType } from './pathfinding';
import { NODE_DECONTAMINATION_RATE, SIMULATION_TICK_RATE, TICKS_PER_MINUTE } from '../constants';
import { createAgent } from './agentFactory';

const isPatient = (agent: Agent) => agent.type.startsWith('PATIENT_');
const isCareRoom = (type: NodeType) =>
  type === NodeType.EXAM_ROOM || type === NodeType.TRAUMA_BAY || type === NodeType.ISOLATION_ROOM;

const processEnvironmentSystem = (nodes: Node[]): Node[] =>
  nodes.map(node => ({
    ...node,
    occupants: [],
    contamination: Math.max(0, (node.contamination || 0) - NODE_DECONTAMINATION_RATE)
  }));

const setTarget = (agent: Agent, targetNodeId: string | null) => {
  agent.targetNodeId = targetNodeId;
  agent.path = [];
  if (targetNodeId && agent.state !== AgentState.EXITING) agent.state = AgentState.MOVING;
};

const releaseStaffAssignment = (staff: Agent, patient?: Agent) => {
  staff.assignedPatientId = null;
  staff.state = AgentState.IDLE;
  if (patient) {
    patient.assignedStaffId = null;
    patient.state = AgentState.IDLE;
  }
};

const processStaffAI = (
  staff: Agent,
  nodes: Node[],
  tick: number,
  agents: Agent[],
  config: SimulationConfig
) => {
  if (staff.state === AgentState.PROCESSING) {
    const patient = agents.find(candidate => candidate.id === staff.assignedPatientId);
    if (!patient) {
      releaseStaffAssignment(staff);
      return;
    }

    const treatmentNode = nodes.find(node => node.id === patient.currentNodeId);
    const duration = Math.max(1, treatmentNode?.processingTime || 1);
    if (tick - staff.tickOnArrival >= duration) releaseStaffAssignment(staff, patient);
    return;
  }

  if (staff.targetNodeId) return;

  if (staff.assignedPatientId) {
    const patient = agents.find(candidate => candidate.id === staff.assignedPatientId);
    if (!patient || patient.patience <= 0) {
      releaseStaffAssignment(staff, patient);
      return;
    }
    if (staff.currentNodeId !== patient.currentNodeId) {
      setTarget(staff, patient.currentNodeId);
      return;
    }

    staff.state = AgentState.PROCESSING;
    staff.tickOnArrival = tick;
    patient.state = AgentState.PROCESSING;
    patient.tickFirstSeen ??= tick;
    return;
  }

  const candidates = agents
    .filter(candidate => {
      if (!isPatient(candidate) || candidate.state !== AgentState.WAITING_FOR_CARE || candidate.assignedStaffId) return false;
      const node = nodes.find(item => item.id === candidate.currentNodeId);
      return Boolean(node && isCareRoom(node.type));
    })
    .sort((a, b) =>
      config.staffPrioritization === 'ACUITY'
        ? a.acuity - b.acuity || a.tickOnArrival - b.tickOnArrival
        : a.tickOnArrival - b.tickOnArrival
    );

  const patient = candidates[0];
  if (patient) {
    staff.assignedPatientId = patient.id;
    patient.assignedStaffId = staff.id;
    if (staff.currentNodeId === patient.currentNodeId) {
      staff.state = AgentState.PROCESSING;
      staff.tickOnArrival = tick;
      patient.state = AgentState.PROCESSING;
      patient.tickFirstSeen ??= tick;
    } else {
      setTarget(staff, patient.currentNodeId);
    }
    return;
  }

  const staffPost =
    findNearestNodeByType(staff.currentNodeId!, NodeType.STAFF_STATION, nodes, false) ||
    findNearestNodeByType(staff.currentNodeId!, NodeType.STAFF_REST, nodes, false);
  if (staffPost) setTarget(staff, staffPost);
};

const processPatientAI = (
  agent: Agent,
  nodes: Node[],
  tick: number,
  config: SimulationConfig
) => {
  if (agent.targetNodeId || agent.state === AgentState.PROCESSING) return;

  const currentNode = nodes.find(node => node.id === agent.currentNodeId);
  if (!currentNode) {
    agent.state = AgentState.IDLE;
    return;
  }

  const elapsedAtNode = tick - agent.tickOnArrival;
  const isCritical = agent.acuity === 1;
  let nextTargetType: NodeType | null = null;

  switch (currentNode.type) {
    case NodeType.ENTRANCE:
    case NodeType.AMBULANCE_ENTRANCE:
      if (agent.state === AgentState.EXITING) return;
      if (agent.infected && config.strictIsolationProtocol) nextTargetType = NodeType.ISOLATION_ROOM;
      else if (isCritical && (config.skipTriageForCritical || config.fastTrackTrauma)) nextTargetType = NodeType.TRAUMA_BAY;
      else nextTargetType = NodeType.RECEPTION;
      break;

    case NodeType.RECEPTION:
      if (elapsedAtNode < currentNode.processingTime) {
        agent.state = AgentState.WAITING;
        return;
      }
      nextTargetType = NodeType.TRIAGE;
      break;

    case NodeType.TRIAGE:
      if (elapsedAtNode < currentNode.processingTime) {
        agent.state = AgentState.WAITING;
        return;
      }
      if (config.diagnosticsFirst && agent.needsLab && !agent.labDone) nextTargetType = NodeType.LABORATORY;
      else if (config.diagnosticsFirst && agent.needsImaging && !agent.imagingDone) nextTargetType = NodeType.RADIOLOGY;
      else nextTargetType = isCritical ? NodeType.TRAUMA_BAY : NodeType.WAITING;
      break;

    case NodeType.WAITING:
    case NodeType.HALLWAY:
      if (elapsedAtNode < 100) {
        agent.state = AgentState.WAITING;
        return;
      }
      nextTargetType = NodeType.EXAM_ROOM;
      break;

    case NodeType.EXAM_ROOM:
    case NodeType.TRAUMA_BAY:
    case NodeType.ISOLATION_ROOM:
      if (agent.state === AgentState.WAITING_FOR_CARE) return;
      if (agent.needsLab && !agent.labDone) {
        agent.tickLabOrdered ??= tick;
        nextTargetType = NodeType.LABORATORY;
      } else if (agent.needsImaging && !agent.imagingDone) {
        nextTargetType = NodeType.RADIOLOGY;
      } else if (agent.needsPharmacy) {
        nextTargetType = NodeType.PHARMACY;
      } else if (Math.random() < config.admissionRate / 100) {
        nextTargetType = NodeType.WARD;
      } else {
        agent.state = AgentState.EXITING;
        nextTargetType = NodeType.ENTRANCE;
      }
      break;

    case NodeType.LABORATORY:
      if (elapsedAtNode < config.labProcessingTime) {
        agent.state = AgentState.WAITING;
        return;
      }
      agent.labDone = true;
      agent.tickLabResult = tick;
      nextTargetType = NodeType.EXAM_ROOM;
      break;

    case NodeType.RADIOLOGY:
      if (elapsedAtNode < currentNode.processingTime) {
        agent.state = AgentState.WAITING;
        return;
      }
      agent.imagingDone = true;
      nextTargetType = NodeType.EXAM_ROOM;
      break;

    case NodeType.PHARMACY:
      if (elapsedAtNode < currentNode.processingTime) {
        agent.state = AgentState.WAITING;
        return;
      }
      agent.needsPharmacy = false;
      agent.state = AgentState.EXITING;
      nextTargetType = NodeType.ENTRANCE;
      break;

    case NodeType.WARD:
      if (elapsedAtNode < config.dischargeProcessTime) {
        agent.state = AgentState.WAITING;
        return;
      }
      agent.state = AgentState.EXITING;
      nextTargetType = NodeType.ENTRANCE;
      break;

    default:
      nextTargetType = NodeType.RECEPTION;
  }

  if (!nextTargetType) return;

  let targetId = findNearestNodeByType(agent.currentNodeId!, nextTargetType, nodes, true);
  if (!targetId && nextTargetType !== NodeType.WAITING) {
    const fallbackType = config.allowHallwayWaiting ? NodeType.HALLWAY : NodeType.WAITING;
    targetId = findNearestNodeByType(agent.currentNodeId!, fallbackType, nodes, false);
  }
  setTarget(agent, targetId);
};

const processAISystem = (
  agent: Agent,
  nodes: Node[],
  tick: number,
  agents: Agent[],
  config: SimulationConfig
) => {
  if (isPatient(agent)) processPatientAI(agent, nodes, tick, config);
  else processStaffAI(agent, nodes, tick, agents, config);
};

const completeArrival = (agent: Agent, tick: number) => {
  agent.targetNodeId = null;
  agent.progress = 0;
  agent.tickOnArrival = tick;
  if (agent.state !== AgentState.EXITING) {
    agent.state = isPatient(agent) ? AgentState.WAITING_FOR_CARE : AgentState.IDLE;
  }
};

const processMovementSystem = (
  agent: Agent,
  nodes: Node[],
  nodeMap: Map<string, Node>,
  config: SimulationConfig,
  speedMultiplier: number,
  layoutVersion: number,
  tick: number
) => {
  if (!agent.targetNodeId) return;

  if (agent.path.length === 0) {
    const path = findPath(
      agent.currentNodeId!,
      agent.targetNodeId,
      nodes,
      config.avoidContamination,
      config.congestionAwareness,
      layoutVersion
    );
    if (!path) {
      agent.targetNodeId = null;
      agent.state = AgentState.IDLE;
      return;
    }
    path.shift();
    agent.path = path;
    if (agent.path.length === 0) {
      completeArrival(agent, tick);
      return;
    }
  }

  let speed = isPatient(agent) ? config.basePatientSpeed : config.baseStaffSpeed;
  if (agent.type === AgentType.PATIENT_INFECTIOUS) speed *= 0.7;
  if (agent.type === AgentType.DOCTOR && agent.assignedPatientId) speed *= 1.3;
  speed *= (nodeMap.get(agent.currentNodeId!)?.speedModifier || 1) * speedMultiplier;

  let distanceBudget = Math.max(0, speed);
  while (distanceBudget > 0 && agent.path.length > 0) {
    const currentNode = nodeMap.get(agent.currentNodeId!);
    const nextNode = nodeMap.get(agent.path[0]);
    if (!currentNode || !nextNode) {
      agent.path = [];
      agent.targetNodeId = null;
      agent.state = AgentState.IDLE;
      return;
    }

    const edgeLength = Math.hypot(nextNode.x - currentNode.x, nextNode.y - currentNode.y);
    if (edgeLength === 0) {
      agent.currentNodeId = agent.path.shift()!;
      agent.progress = 0;
      continue;
    }

    const remainingDistance = edgeLength * (1 - agent.progress);
    const travelled = Math.min(distanceBudget, remainingDistance);
    agent.progress += travelled / edgeLength;
    agent.totalDistancePixels += travelled;
    distanceBudget -= travelled;

    if (agent.progress >= 1 - Number.EPSILON) {
      agent.currentNodeId = agent.path.shift()!;
      agent.progress = 0;
      agent.x = nextNode.x;
      agent.y = nextNode.y;
      if (agent.path.length === 0) completeArrival(agent, tick);
    } else {
      agent.x = currentNode.x + (nextNode.x - currentNode.x) * agent.progress;
      agent.y = currentNode.y + (nextNode.y - currentNode.y) * agent.progress;
    }
  }
};

const assignOccupants = (nodes: Node[], agents: Agent[]) => {
  const occupantsByNode = new Map<string, string[]>();
  for (const agent of agents) {
    if (!agent.currentNodeId) continue;
    const occupants = occupantsByNode.get(agent.currentNodeId) || [];
    occupants.push(agent.id);
    occupantsByNode.set(agent.currentNodeId, occupants);
  }
  return nodes.map(node => ({ ...node, occupants: occupantsByNode.get(node.id) || [] }));
};

export const processSimulationTick = (
  currentNodes: Node[],
  currentAgents: Agent[],
  currentStats: SimulationStats,
  isRunning: boolean,
  isAutoSpawning: boolean,
  autoSpawnRate: number,
  simulationSpeed: number,
  simConfig: SimulationConfig,
  patientPatience: number,
  simTickCounter: number,
  layoutVersion = 0,
  pixelsPerMeter = 20
): { nodes: Node[]; agents: Agent[]; stats: SimulationStats } => {
  if (!isRunning) return { nodes: currentNodes, agents: currentAgents, stats: currentStats };

  const agents = currentAgents.map(agent => ({
    ...agent,
    path: [...agent.path],
    totalActiveTime: agent.totalActiveTime || 0,
    tickCreated: agent.tickCreated ?? agent.tickOnArrival
  }));
  let nodes = assignOccupants(processEnvironmentSystem(currentNodes), agents);

  if (isAutoSpawning && nodes.length > 0) {
    const ticksPerSecond = 1000 / SIMULATION_TICK_RATE;
    const spawnChance = autoSpawnRate / 3600 / ticksPerSecond;
    if (Math.random() < spawnChance) {
      const entrance =
        nodes.find(node => node.type === NodeType.ENTRANCE) ||
        nodes.find(node => node.type === NodeType.AMBULANCE_ENTRANCE);
      if (entrance) {
        const types = [AgentType.PATIENT_WAL_IN, AgentType.PATIENT_EMS, AgentType.PATIENT_INFECTIOUS];
        agents.push(createAgent(types[Math.floor(Math.random() * types.length)], entrance, simTickCounter, patientPatience, simConfig));
      }
    }
  }

  const nodeMap = new Map(nodes.map(node => [node.id, node]));
  for (const agent of agents) {
    processAISystem(agent, nodes, simTickCounter, agents, simConfig);
    processMovementSystem(agent, nodes, nodeMap, simConfig, simulationSpeed, layoutVersion, simTickCounter);
    if (isPatient(agent) && (agent.state === AgentState.WAITING || agent.state === AgentState.WAITING_FOR_CARE)) {
      agent.totalWaitTime += 1;
      agent.patience -= simConfig.patienceDepletionRate;
    }
    if (!isPatient(agent) && agent.state === AgentState.PROCESSING) agent.totalActiveTime += 1;
    if (agent.infected) {
      const node = nodeMap.get(agent.currentNodeId!);
      if (node) node.contamination = Math.min(100, node.contamination + simConfig.nodeContaminationRate);
    }
  }

  const ticksPerSecond = 1000 / SIMULATION_TICK_RATE;
  const infectionRadiusPixels = simConfig.infectionRadius * pixelsPerMeter;
  let newInfections = 0;
  for (const agent of agents) {
    if (agent.infected) continue;
    const exposed = agents.some(source =>
      source.id !== agent.id &&
      source.infected &&
      Math.hypot(source.x - agent.x, source.y - agent.y) <= infectionRadiusPixels
    );
    if (exposed && Math.random() < Math.min(1, simConfig.infectionProbability / ticksPerSecond)) {
      agent.infected = true;
      newInfections += 1;
    }
  }

  const completedPatients = agents.filter(agent =>
    isPatient(agent) && agent.state === AgentState.EXITING && agent.path.length === 0 && !agent.targetNodeId
  );
  const lwbsPatients = agents.filter(agent => isPatient(agent) && agent.patience <= 0);
  const remainingAgents = agents.filter(agent =>
    (!isPatient(agent) || agent.patience > 0) &&
    !(agent.state === AgentState.EXITING && agent.path.length === 0 && !agent.targetNodeId)
  );
  const activePatients = remainingAgents.filter(isPatient);
  nodes = assignOccupants(nodes, remainingAgents);

  const doctors = remainingAgents.filter(agent => agent.type === AgentType.DOCTOR);
  const nurses = remainingAgents.filter(agent => agent.type === AgentType.NURSE);
  const assistants = remainingAgents.filter(agent => agent.type === AgentType.NURSE_ASSISTANT);
  const roleDistance = {
    patients: activePatients.reduce((sum, agent) => sum + agent.totalDistancePixels, 0),
    doctors: doctors.reduce((sum, agent) => sum + agent.totalDistancePixels, 0),
    nurses: nurses.reduce((sum, agent) => sum + agent.totalDistancePixels, 0),
    assistants: assistants.reduce((sum, agent) => sum + agent.totalDistancePixels, 0)
  };
  const utilization = (staff: Agent[]) =>
    staff.length && simTickCounter > 0
      ? staff.reduce((sum, agent) => sum + agent.totalActiveTime, 0) / (staff.length * simTickCounter)
      : 0;

  const previousTreated = currentStats.treatedPatients;
  const totalTreated = previousTreated + completedPatients.length;
  const averageCompletedMetric = (
    previousAverage: number,
    values: number[]
  ) => values.length
    ? (previousAverage * previousTreated + values.reduce((sum, value) => sum + value, 0)) / totalTreated
    : previousAverage;

  return {
    nodes,
    agents: remainingAgents,
    stats: {
      ...currentStats,
      activePatients: activePatients.length,
      treatedPatients: totalTreated,
      lwbs: currentStats.lwbs + lwbsPatients.length,
      infections: currentStats.infections + newInfections,
      avgDistanceByRole: roleDistance,
      avgDistanceTraveled: activePatients.length ? roleDistance.patients / activePatients.length / pixelsPerMeter : 0,
      staffUtilization: {
        doctors: utilization(doctors),
        nurses: utilization(nurses),
        assistants: utilization(assistants)
      },
      avgWaitTime: activePatients.length
        ? activePatients.reduce((sum, agent) => sum + agent.totalWaitTime, 0) / activePatients.length / TICKS_PER_MINUTE
        : currentStats.avgWaitTime,
      avgDoorToDoc: averageCompletedMetric(
        currentStats.avgDoorToDoc,
        completedPatients
          .filter(agent => agent.tickFirstSeen !== null)
          .map(agent => (agent.tickFirstSeen! - agent.tickCreated) / TICKS_PER_MINUTE)
      ),
      avgLengthOfStay: averageCompletedMetric(
        currentStats.avgLengthOfStay,
        completedPatients.map(agent => (simTickCounter - agent.tickCreated) / TICKS_PER_MINUTE)
      ),
      avgLabTurnaround: averageCompletedMetric(
        currentStats.avgLabTurnaround,
        completedPatients
          .filter(agent => agent.tickLabOrdered !== null && agent.tickLabResult !== null)
          .map(agent => (agent.tickLabResult! - agent.tickLabOrdered!) / TICKS_PER_MINUTE)
      )
    }
  };
};

export { createAgent } from './agentFactory';
